import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/app/api";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { showDesktopAlert } from "@/lib/desktop-alerts";
import { isRemembered } from "@/lib/auth";
import { forgetPushToken, registerPush, storedPushToken } from "@/lib/push";
import { apiErrorMessage } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { useQueryErrorToast } from "@/lib/use-query-error-toast";
import type { InboxItem } from "@/types/api";
import {
  useGetInboxUnreadCountQuery,
  useListInboxQuery,
  useMarkAllInboxReadMutation,
  useMarkInboxReadMutation,
  useRegisterPushTokenMutation,
  useUnregisterPushTokenMutation,
} from "./notifications-api";

/**
 * The badge poll. The count rides a partial index, so this is cheap — cheap
 * enough to keep running while the tab is in the background, which is exactly
 * when a desktop alert is worth raising.
 */
const UNREAD_POLL_MS = 20_000;

/**
 * The unread slice of the inbox, shared by the badge, the popover and the
 * critical banner.
 *
 * Only the count is polled — the API is explicit that `/inbox` is not for
 * polling. The unread page is fetched once and refetched when the polled count
 * moves, which is the only time it can have changed without this tab having
 * caused it.
 *
 * Skipped outside a live session: an `unavailable` shell has a token but no
 * server, and polling it every minute only adds to the noise.
 */
export function useUnreadInbox() {
  const signedIn = useAppSelector((state) => state.session.status === "signed-in");

  const count = useGetInboxUnreadCountQuery(undefined, {
    skip: !signedIn,
    pollingInterval: UNREAD_POLL_MS,
    refetchOnFocus: true,
  });
  const unread = useListInboxQuery({ unread_only: true, limit: 50 }, { skip: !signedIn });

  return {
    signedIn,
    unreadCount: count.data?.unread_count ?? 0,
    unreadItems: unread.data?.items ?? [],
    count,
    unread,
  };
}

/**
 * Keeps the unread page in step with the polled count, and re-reads `/me` when
 * a `role_changed` notice arrives.
 *
 * Mounted once, by the inbox menu in the top bar. A role change means the
 * permissions this shell is gating on are stale; an admin narrowed mid-shift
 * whose next click is a 403 will read it as an outage unless the panel has
 * already caught up.
 */
export function useInboxSync() {
  const dispatch = useAppDispatch();
  const { count, unread, unreadItems } = useUnreadInbox();

  // Here, not in `useUnreadInbox`: that hook has three callers, this one has
  // one, so the toast is raised once. Without it a failing poll leaves the
  // badge on its last count and hides any critical notice, with no sign why.
  useQueryErrorToast(count, "Couldn't check for new notifications");
  useQueryErrorToast(unread, "Couldn't load your unread notifications");

  const polled = count.data?.unread_count;
  const listed = unread.data?.unread_count;
  const refetchUnread = unread.refetch;
  // The count a refetch was last issued for. Without it a notice arriving
  // between the two requests would leave them disagreeing, and this effect
  // would refetch in a loop until the next poll.
  const syncedFor = useRef<number | null>(null);

  useEffect(() => {
    if (polled === undefined || listed === undefined) return;
    // In step again: forget the guard. Kept, it would swallow the next notice
    // whenever the count lands back on the same number — read one, receive one,
    // and "1" had already been synced for, so the list never refetched.
    if (polled === listed) {
      syncedFor.current = null;
      return;
    }
    if (syncedFor.current !== polled) {
      syncedFor.current = polled;
      void refetchUnread();
    }
  }, [polled, listed, refetchUnread]);

  // Notices already unread when the shell loaded are not news — the badge
  // shows them. Only ones that turn up afterwards get a toast and a desktop
  // alert, and a role change among them re-reads `/me` as well.
  const seen = useRef<Set<string> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!unread.data) return;
    const items = unread.data.items;

    if (seen.current === null) {
      seen.current = new Set(items.map((item) => item.id));
      return;
    }

    const fresh = items.filter((item) => !seen.current?.has(item.id));
    if (fresh.length === 0) return;
    for (const item of fresh) seen.current.add(item.id);

    if (fresh.some((item) => item.type === "role_changed")) {
      dispatch(api.util.invalidateTags(["Profile"]));
    }

    // Oldest first, so a burst reads in the order it was sent. More than three
    // at once collapse into one summary rather than a wall of toasts.
    const ordered = [...fresh].reverse();
    if (ordered.length > 3) {
      toast.info(`${String(ordered.length)} new notifications`, {
        description: "Open the bell to read them.",
      });
      showDesktopAlert({
        tag: "inbox-burst",
        title: `${String(ordered.length)} new notifications`,
        body: "Algorithm Arena admin panel",
      });
      return;
    }

    for (const item of ordered) {
      const path = internalPath(item.action_url);
      const title = item.type === "role_changed" ? "Your permissions changed" : item.title;
      const body =
        item.type === "role_changed" ? "The panel has reloaded what you can access." : item.body;

      toast.info(title, {
        description: body,
        ...(path ? { action: { label: "Open", onSelect: () => void navigate(path) } } : {}),
      });
      showDesktopAlert({
        tag: item.id,
        title,
        body,
        onClick: path ? () => void navigate(path) : undefined,
      });
    }
  }, [unread.data, dispatch, navigate]);

  return { unreadItems };
}

/** Only same-panel paths are followed; anything else could be an open redirect. */
export function internalPath(url: string | null): string | null {
  return url && url.startsWith("/") && !url.startsWith("//") ? url : null;
}

/**
 * Keeps this browser registered for push while an admin is signed in.
 *
 * Runs on every sign-in and page load, and again whenever `register` is
 * called — the desktop-alerts dialog calls it right after permission is
 * granted. Re-registering is cheap and idempotent, and it is what hands the
 * browser to whichever admin is signed in now.
 *
 * Only for a "Keep me signed in" session. Push reaches the browser with every
 * tab closed, so a session that ends with the browser would otherwise keep
 * receiving notices after it ended. Such a session instead drops any token
 * this browser still holds from an earlier remembered one, and relies on the
 * in-page alerts, which stop when the tab does.
 */
export function usePushRegistration(signedIn: boolean) {
  const [save] = useRegisterPushTokenMutation();
  const [drop] = useUnregisterPushTokenMutation();

  const register = async () => {
    if (isRemembered()) return registerPush((token) => save(token).unwrap());
    const stale = storedPushToken();
    if (stale) {
      await drop(stale)
        .unwrap()
        .then(forgetPushToken)
        .catch(() => {
          // Kept, so the next page load tries again.
        });
    }
    return false;
  };

  useEffect(() => {
    if (signedIn) void register();
    // `register` is rebuilt every render; only a sign-in should re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  return register;
}

/**
 * Mark-read actions that also re-read `/me` when the notice being cleared was
 * a role change — the moment the admin acknowledges it is the moment the
 * shell should certainly reflect it.
 */
export function useMarkRead() {
  const dispatch = useAppDispatch();
  const [markOne] = useMarkInboxReadMutation();
  const [markAll, markingAll] = useMarkAllInboxReadMutation();

  const read = (item: InboxItem) => {
    if (item.read_at !== null) return;
    void markOne(item.id)
      .unwrap()
      .then(() => {
        if (item.type === "role_changed") dispatch(api.util.invalidateTags(["Profile"]));
      })
      .catch((err: unknown) => {
        // The row stays unread and the next click tries again — but say so,
        // or the critical banner just sits there looking ignored.
        toast.error("Couldn't mark that as read", { description: apiErrorMessage(err) });
      });
  };

  const readAll = async (unreadItems: InboxItem[]) => {
    try {
      await markAll().unwrap();
      if (unreadItems.some((item) => item.type === "role_changed")) {
        dispatch(api.util.invalidateTags(["Profile"]));
      }
    } catch (err) {
      toast.error("Couldn't mark everything read", { description: apiErrorMessage(err) });
    }
  };

  return { read, readAll, markingAll: markingAll.isLoading };
}
